import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Heart } from 'lucide-react';
import { aboutContent, images } from '../data/mock';

const About = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <span className="text-primary text-sm uppercase tracking-wider font-medium mb-4 block">
                {aboutContent.subtitle}
              </span>
              <h1 className="font-heading text-4xl md:text-5xl font-medium text-foreground mb-8">
                {aboutContent.title}
              </h1>
              {aboutContent.paragraphs.map((paragraph, index) => (
                <p
                  key={index}
                  className="text-muted-foreground leading-relaxed mb-4"
                >
                  {paragraph}
                </p>
              ))}
            </div>
            <div className="order-1 lg:order-2 relative">
              <img
                src={aboutContent.image}
                alt="Julie Masse - Praticienne Amma assis"
                className="w-full h-[600px] object-cover"
              />
              <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-primary/10" />
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24 section-gradient">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-medium text-foreground mb-4">
              Mes valeurs
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Des principes qui guident chacune de mes interventions.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {aboutContent.values.map((value, index) => (
              <div
                key={index}
                className="p-8 bg-white border border-border text-center card-hover"
              >
                <div className="w-14 h-14 mx-auto mb-6 bg-primary/10 flex items-center justify-center">
                  <Heart className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-heading text-xl font-medium text-foreground mb-2">
                  {value.title}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Journey Section */}
      <section className="py-24">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-medium text-foreground mb-4">
              Mon parcours
            </h2>
          </div>
          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-4 lg:left-1/2 top-0 bottom-0 w-px bg-border lg:transform lg:-translate-x-1/2" />
            
            {/* Timeline items */}
            <div className="space-y-12">
              <div className="relative flex items-start gap-8 lg:gap-0">
                <div className="flex-shrink-0 w-8 h-8 bg-primary flex items-center justify-center z-10">
                  <span className="text-white text-sm font-medium">1</span>
                </div>
                <div className="lg:w-1/2 lg:pr-12 lg:text-right lg:ml-auto">
                  <h3 className="font-heading text-xl font-medium text-foreground mb-2">
                    Diplôme d'État Aide-soignante
                  </h3>
                  <p className="text-muted-foreground">
                    Formation initiale dans le soin et l'accompagnement des personnes. 
                    Développement d'une écoute attentive et d'un sens aigu du toucher thérapeutique.
                  </p>
                </div>
              </div>
              
              <div className="relative flex items-start gap-8 lg:gap-0">
                <div className="flex-shrink-0 w-8 h-8 bg-primary flex items-center justify-center z-10 lg:absolute lg:left-1/2 lg:transform lg:-translate-x-1/2">
                  <span className="text-white text-sm font-medium">2</span>
                </div>
                <div className="lg:w-1/2 lg:pl-12">
                  <h3 className="font-heading text-xl font-medium text-foreground mb-2">
                    Formation en médecine alternative
                  </h3>
                  <p className="text-muted-foreground">
                    Reconversion vers les techniques de relaxation et de bien-être. 
                    Exploration des approches holistiques de la santé.
                  </p>
                </div>
              </div>
              
              <div className="relative flex items-start gap-8 lg:gap-0">
                <div className="flex-shrink-0 w-8 h-8 bg-primary flex items-center justify-center z-10">
                  <span className="text-white text-sm font-medium">3</span>
                </div>
                <div className="lg:w-1/2 lg:pr-12 lg:text-right lg:ml-auto">
                  <h3 className="font-heading text-xl font-medium text-foreground mb-2">
                    Certification Amma assis
                  </h3>
                  <p className="text-muted-foreground">
                    Spécialisation dans cette technique japonaise ancestrale, 
                    particulièrement adaptée au monde de l'entreprise.
                  </p>
                </div>
              </div>
              
              <div className="relative flex items-start gap-8 lg:gap-0">
                <div className="flex-shrink-0 w-8 h-8 bg-primary flex items-center justify-center z-10 lg:absolute lg:left-1/2 lg:transform lg:-translate-x-1/2">
                  <span className="text-white text-sm font-medium">4</span>
                </div>
                <div className="lg:w-1/2 lg:pl-12">
                  <h3 className="font-heading text-xl font-medium text-foreground mb-2">
                    Lancement de Julie Masse
                  </h3>
                  <p className="text-muted-foreground">
                    Création de mon activité d'interventions en entreprise, 
                    pour apporter bien-être et sérénité sur le lieu de travail.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-primary/5">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-medium text-foreground mb-6">
            Envie de collaborer ?
          </h2>
          <p className="text-muted-foreground mb-10 max-w-2xl mx-auto leading-relaxed">
            Je serais ravie d'échanger avec vous sur vos besoins en bien-être au travail.
          </p>
          <Link to="/contact" className="btn-primary">
            Me contacter
            <ArrowRight className="w-4 h-4 ml-2" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default About;
